import { useState, type CSSProperties } from 'react';
import type { ProductCardItem, ProductCardsMetadata } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { messageDir } from '../../../../lib/message-direction';
import { buyProductMessage, productExpertLines, productRatingLine } from '../../../../lib/product-cards';
import { Modal } from '../../../Modal';
import { Button, Card } from '../../ui';
import styles from './ProductCards.module.css';

/**
 * A shopping reply's products (`metadata.productCards`), drawn in place of their pictures: a row of up to 3 cards,
 * each opening a details view with "Buy it for me" and "Open in store" ("Book it for me" and "Open website" on a
 * place the agent recommended). The buy button sends an ordinary user message, so buying or booking runs through the
 * agent as if the user typed it. Absent `onBuy` (a read-only thread), the details view shows no buy button.
 */
export const ProductCards = ({ card, onBuy }: { card: ProductCardsMetadata; onBuy?: (text: string) => void }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const [open, setOpen] = useState<ProductCardItem | null>(null);
  const { logPageClickBi, logModalActionBi } = useGenericBi();
  const modalAction = (action: string) => logModalActionBi({ page_name: 'chat', modal_name: 'product-card', action });

  return (
    <>
      <ul
        className={styles.row}
        aria-label={t(card.items.every((item) => item.kind === 'place') ? 'chat.productCards.placesAria' : 'chat.productCards.aria')}
        data-testid="chat-product-cards"
        style={{ '--card-count': card.items.length } as CSSProperties}
      >
        {card.items.map((item, index) => (
          <li key={item.attachmentUrl} className={styles.cell}>
            <Card
              component="button"
              variant="outline"
              size="sm"
              interactive
              disablePadding
              className={styles.card}
              onClick={() => {
                logPageClickBi(
                  { page_name: 'chat', button_name: 'product-card' },
                  { componentName: 'product-card', additionalFields: { position: String(index + 1), count: String(card.items.length) } },
                );
                setOpen(item);
              }}
            >
              <img className={styles.picture} src={item.attachmentUrl} alt="" loading="lazy" />
              <ProductDetails item={item} locale={locale} />
            </Card>
          </li>
        ))}
      </ul>
      <Modal
        open={open !== null}
        onClose={() => setOpen(null)}
        title={open?.title}
        maxHeightPx={650}
        scrollBody
        footer={
          open && (
            <div className={styles.actions}>
              {open.productUrl && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    modalAction(open.kind === 'place' ? 'open-website' : 'open-in-store');
                    window.open(open.productUrl, '_blank', 'noopener,noreferrer');
                  }}
                >
                  {t(open.kind === 'place' ? 'chat.productCards.openWebsite' : 'chat.productCards.openInStore')}
                </Button>
              )}
              {onBuy && (
                <Button
                  variant="contained"
                  onClick={() => {
                    modalAction(open.kind === 'place' ? 'book-it-for-me' : 'buy-it-for-me');
                    onBuy(buyProductMessage(open));
                    setOpen(null);
                  }}
                >
                  {t(open.kind === 'place' ? 'chat.productCards.book' : 'chat.productCards.buy')}
                </Button>
              )}
            </div>
          )
        }
      >
        {open && (
          <div className={styles.details}>
            <img className={styles.bigPicture} src={open.attachmentUrl} alt={open.title} />
            <ProductDetails item={open} locale={locale} withTitle={false} />
          </div>
        )}
      </Modal>
    </>
  );
};

function ProductDetails({ item, locale, withTitle = true }: { item: ProductCardItem; locale: string; withTitle?: boolean }) {
  const expertLines = productExpertLines(item);
  const rating = expertLines.length > 0 ? null : productRatingLine(item, locale);
  return (
    <span className={styles.text}>
      {withTitle && (
        <span className={styles.title} dir="auto">
          {item.title}
        </span>
      )}
      {item.price && (
        <span className={styles.price} dir="auto">
          {item.price}
        </span>
      )}
      {item.store && (
        <span className={styles.store} dir="auto">
          {item.store}
        </span>
      )}
      {expertLines.map((line) => (
        <span key={line} className={styles.expert} dir={messageDir(line)}>
          {line}
        </span>
      ))}
      {rating && <span className={styles.rating}>{rating}</span>}
    </span>
  );
}
